import { useState, useCallback, useRef } from 'react';
import { streamChat, type StreamEvent } from '../lib/sse';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string;
  model?: string | null;
  streaming?: boolean;
  toolCalls?: ToolCall[];
  usage?: StreamEvent['usage'];
}

export interface ToolCall {
  id: string;
  name: string;
  args?: Record<string, unknown>;
  result?: unknown;
  status: 'pending' | 'running' | 'success' | 'error';
}

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const toolAccRef = useRef<Record<string, string>>({});

  const sendMessage = useCallback(async (
    content: string,
    conversationId: string | null,
    model?: string,
  ) => {
    setError(null);

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
    };

    const assistantMsg: ChatMessage = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      streaming: true,
      toolCalls: [],
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setIsStreaming(true);

    const abort = new AbortController();
    abortRef.current = abort;
    toolAccRef.current = {};

    await streamChat({
      conversationId,
      content,
      model,
      signal: abort.signal,
      onEvent: (event: StreamEvent) => {
        setMessages(prev => {
          const msgs = [...prev];
          const last = { ...msgs[msgs.length - 1] };

          switch (event.type) {
            case 'text_delta':
              last.content += event.text ?? '';
              break;
            case 'message_start':
              last.model = event.model ?? null;
              break;
            case 'tool_use_start':
              if (event.tool) {
                last.toolCalls = [...(last.toolCalls ?? []), {
                  id: event.tool.id,
                  name: event.tool.name,
                  status: 'pending',
                }];
              }
              break;
            case 'tool_use_delta':
              if (event.tool) {
                toolAccRef.current[event.tool.id] = (toolAccRef.current[event.tool.id] ?? '') + (event.text ?? '');
              }
              break;
            case 'tool_use_end':
              if (event.tool) {
                last.toolCalls = (last.toolCalls ?? []).map(tc =>
                  tc.id === event.tool!.id
                    ? { ...tc, status: 'running' as const, args: tryParse(toolAccRef.current[tc.id]) }
                    : tc
                );
              }
              break;
            case 'tool_result':
              if (event.tool) {
                last.toolCalls = (last.toolCalls ?? []).map(tc =>
                  tc.id === event.tool!.id
                    ? { ...tc, status: (event.tool!.status ?? 'success') as ToolCall['status'], result: event.tool!.result }
                    : tc
                );
              }
              break;
            case 'message_end':
              last.streaming = false;
              last.usage = event.usage;
              break;
            case 'error':
              last.streaming = false;
              last.content += `\n\n⚠️ ${event.error?.message ?? 'Okänt fel'}`;
              break;
          }

          msgs[msgs.length - 1] = last;
          return msgs;
        });
      },
      onDone: () => setIsStreaming(false),
      onError: (err) => {
        setError(err.message);
        setIsStreaming(false);
        setMessages(prev => {
          const msgs = [...prev];
          const last = { ...msgs[msgs.length - 1], streaming: false };
          if (!last.content) last.content = '⚠️ Kunde inte nå servern.';
          msgs[msgs.length - 1] = last;
          return msgs;
        });
      },
    });
  }, []);

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  const loadMessages = useCallback((msgs: ChatMessage[]) => {
    setMessages(msgs);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return { messages, isStreaming, error, sendMessage, stopStreaming, loadMessages, clearMessages };
}

function tryParse(str?: string): Record<string, unknown> | undefined {
  if (!str) return undefined;
  try { return JSON.parse(str); } catch { return undefined; }
}
